import { useState } from "react";
import { Col, Container, Row, } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { removeHTMLTagsString } from "../../helpers/utils";
import { EditCommunicationLogModal } from "../../components/CommunicationLog/EditCommunicationLogModal";
import {
  ColumnRender,
  FilterType,
  IColumn,
  IFilter,
  IPagedListAction,
} from "../PagedList/Declarations";
import { PagedList } from "../PagedList/PagedList";
import { useAtom } from "@dbeining/react-atom";
import { userAtom } from "../../atoms/userAtom";
import { Role } from "../../helpers";
import { IRole } from "../../customTypings/role";
import { ICommunicationLog } from "@/customTypings/CommunicationLog";
import { faPlus } from "@fortawesome/free-solid-svg-icons";

type CommunicationLogProps = {
  myCommunicationLogs: boolean;
  leadId?: string;
  customerId?: string;
};

function CommunicationLog({ myCommunicationLogs, leadId, customerId }: CommunicationLogProps) {
  const navigate = useNavigate();
  const [refreshNumber, setRefreshNumber] = useState(0);
  const [showEditModal, setShowEditModal] = useState(false);
  const [item, setItem] = useState<ICommunicationLog>({
    id: "",
    customerId: "",
    date: new Date(),
    from: "",
    to: "",
    files: [],
    subject: "",
    body: "",
    leadId: "",
  });
  const columns: IColumn[] = [];
  const filters: IFilter[] = [];
  const headActions: IPagedListAction[] = [];
  const currentUser = useAtom(userAtom);
  const isSalesUser = currentUser?.identity.roles.some(
    (role: IRole) => role.name === Role.Sales
  );

  const staticFilterValues: Record<string, any> = {
    GetAll: !myCommunicationLogs,
  };
  if (leadId)
    staticFilterValues["LeadId"] = leadId;
  if (customerId)
    staticFilterValues["CustomerId"] = customerId;

  const persistanceKey: string = "";

  const changeItem = (logItem: any) => {
    const updatedItem: ICommunicationLog = {
      id: logItem.id,
      date: new Date(logItem.date),
      customerId: logItem.customerId || "",
      leadId: logItem.leadId || "",
      from: logItem.from,
      to: logItem.to,
      subject: logItem.subject,
      body: removeHTMLTagsString(logItem.body),
      files: [],
    };
    setItem(updatedItem);
    setShowEditModal(true);
  };

  filters.push({
    title: "Date/Time",
    member: "date",
    type: FilterType.date,
  } as IFilter);
  filters.push({
    title: "From",
    member: "from",
    type: FilterType.text,
  } as IFilter);
  filters.push({
    title: "To",
    member: "to",
    type: FilterType.text,
  } as IFilter);

  if (!leadId && !customerId) {
    filters.push({
        title: "Customer",
        member: "customerName",
        type: FilterType.text,
    } as IFilter);
  }
  if (!leadId && !customerId) {
    filters.push({
      title: "Lead",
      member: "leadName",
      type: FilterType.text,
    } as IFilter);
  }
  filters.push({
    title: "Filename",
    member: "filename",
    type: FilterType.text,
  } as IFilter);
  filters.push({
    title: "Subject",
    member: "subject",
    type: FilterType.text,
  } as IFilter);
  filters.push({
    title: "Body",
    member: "body",
    type: FilterType.text,
  } as IFilter);

  columns.push({
    member: "date",
    renderAs: ColumnRender.date,
    title: "Date/Time",
    width: 100,
  } as IColumn);
  columns.push({
    member: "from",
    renderAs: ColumnRender.text,
    title: "From",
    width: 100,
  } as IColumn);
  columns.push({
    member: "to",
    renderAs: ColumnRender.text,
    title: "To",
    width: 100,
  } as IColumn);
  if (!leadId && !customerId) {
  columns.push({
    member: "customerName",
    renderAs: ColumnRender.text,
    title: "Customer",
    width: 150,
    } as IColumn);
  }
  if (!leadId && !customerId) {
    columns.push({
      member: "leadName",
      renderAs: ColumnRender.text,
      title: "Lead",
      width: 150,
    } as IColumn);
  }
  columns.push({
    member: "fileName",
    renderAs: ColumnRender.text,
    title: "Files",
    width: 200,
  } as IColumn);
  columns.push({
    member: "subject",
    renderAs: ColumnRender.text,
    title: "Subject",
    width: 100,
  } as IColumn);
  columns.push({
    member: "body",
    renderAs: ColumnRender.synthesize,
    title: "Body",
    width: 100,
  } as IColumn);

  if (!isSalesUser) {
    headActions.push({
      icon: faPlus,
      text: "New Communication Log",
      onClick: () => navigate("/addcommunicationlog"),
    } as IPagedListAction);
  }

  return (
    <Container fluid>
      <Helmet>
        <title>Communication Logs</title>
      </Helmet>
      <Row>
        <Col>
        {(!leadId && !customerId) && <h1 className="my-3">My Communication</h1>}
          <PagedList
            columns={columns}
            headActions={headActions}
            staticFilterValues={staticFilterValues}
            filters={filters}
            itemActions={[
              { text: "Detail", onClick: changeItem, primary: true } as IPagedListAction,
            ]}
            defaultSortBy="date"
            getUrl="api/communicationlog/communicationlogs"
            avatarUrl="api/avatar"
            rowFormatter={(item: any) => null}
            persistanceKey={persistanceKey}
            refreshNumber={refreshNumber}
          />
        </Col>
      </Row>
      <EditCommunicationLogModal
        show={showEditModal}
        onClose={(updated: boolean) => {
          setShowEditModal(false);
          if (updated) setRefreshNumber((prev) => prev + 1);
        }}
        item={item}
      />
    </Container>
  );
}

export default CommunicationLog;